import { getUserLikes, getLikesForTarget } from './likes';

export interface VideoMetadata {
  hash: string;
  title: string;
  description: string;
  uploader: string;
  uploadedAt: number;
  magnetURI?: string;
  fileSize?: number;
  tags: string[];
  thumbnailDataUrl?: string;
}

export interface Pin {
  videoHash: string;
  userId: string;
  pinnedAt: number;
  signature: string;
}

export interface UserProfile {
  userId: string;
  publicKey: string;
  username?: string;
  bio?: string;
  createdAt: number;
  links?: Array<{ label: string; url: string }>;
  accentColor?: string;
  backgroundConfig?: { type: 'solid' | 'pattern' | 'image'; value: string };
  avatarMagnetURI?: string;
  bannerMagnetURI?: string;
  cssProperties?: Record<string, string>;
  pinnedContent?: string[];
  socialGraphPublic?: boolean;
  avatarShape?: 'square' | 'circle';
  bioFont?: 'monospace' | 'sans-serif';
  displayPreference?: 'username' | 'id';
}

const VIDEOS_KEY = 'authenticulture_videos';
const PINS_KEY = 'authenticulture_pins';
const PROFILE_KEY = 'authenticulture_profile';

export function saveVideo(video: VideoMetadata): void {
  const videos = getAllVideos();
  videos.push(video);
  localStorage.setItem(VIDEOS_KEY, JSON.stringify(videos));
}

export function updateVideoThumbnail(hash: string, thumbnailDataUrl: string): void {
  const videos = getAllVideos();
  const video = videos.find(v => v.hash === hash);
  if (video) {
    video.thumbnailDataUrl = thumbnailDataUrl;
    localStorage.setItem(VIDEOS_KEY, JSON.stringify(videos));
  }
}

export function deleteVideo(hash: string, userId: string, force = false): boolean {
  const videos = getAllVideos();
  const index = videos.findIndex(v => v.hash === hash && (force || v.uploader === userId));
  if (index === -1) return false;
  videos.splice(index, 1);
  localStorage.setItem(VIDEOS_KEY, JSON.stringify(videos));
  return true;
}

export function getAllVideos(): VideoMetadata[] {
  const stored = localStorage.getItem(VIDEOS_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function getVideo(hash: string): VideoMetadata | null {
  const videos = getAllVideos();
  return videos.find(v => v.hash === hash) || null;
}

export function savePin(pin: Pin): boolean {
  const pins = getAllPins();
  
  // Check if user already pinned this video
  if (pins.some(p => p.videoHash === pin.videoHash && p.userId === pin.userId)) {
    return false; // Already pinned, don't allow duplicate
  }

  pins.push(pin);
  localStorage.setItem(PINS_KEY, JSON.stringify(pins));
  return true;
}

export function getAllPins(): Pin[] {
  const stored = localStorage.getItem(PINS_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function getPinsForVideo(videoHash: string): Pin[] {
  return getAllPins().filter(p => p.videoHash === videoHash);
}

export function getUserPins(userId: string): Pin[] {
  return getAllPins().filter(p => p.userId === userId);
}

export function userHasPinned(userId: string, videoHash: string): boolean {
  return getAllPins().some(p => p.userId === userId && p.videoHash === videoHash);
}

export function saveProfile(profile: UserProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function getProfile(): UserProfile | null {
  const stored = localStorage.getItem(PROFILE_KEY);
  return stored ? JSON.parse(stored) : null;
}

export function calculateTastemakerScore(userId: string): number {
  let totalScore = 0;

  // Pin-based: early pinning of videos that later go popular
  getUserPins(userId).forEach(pin => {
    const video = getVideo(pin.videoHash);
    if (!video) return;
    const videoPins = getPinsForVideo(pin.videoHash);
    const totalPins = videoPins.length;
    if (totalPins < 2) return;
    const earliestPin = Math.min(...videoPins.map(p => p.pinnedAt));
    const latestPin = Math.max(...videoPins.map(p => p.pinnedAt));
    const earlinessRatio = 1 - (pin.pinnedAt - earliestPin) / (latestPin - earliestPin || 1);
    totalScore += Math.pow(earlinessRatio, 2) * 100 + Math.log10(totalPins + 1) * 10;
  });

  // Like-based: early likes on popular posts/messages (scaled ~40% of pin value)
  for (const like of getUserLikes(userId)) {
    const targetLikes = getLikesForTarget(like.targetId);
    const total = targetLikes.length;
    if (total < 3) continue;
    const times = targetLikes.map(l => l.createdAt).sort((a, b) => a - b);
    const span = (times[total - 1] - times[0]) || 1;
    const earlinessRatio = Math.max(0, 1 - (like.createdAt - times[0]) / span);
    totalScore += Math.pow(earlinessRatio, 2) * 40 + Math.log10(total + 1) * 4;
  }

  return Math.round(totalScore);
}

export function calculatePinScore(userId: string, videoHash: string): number {
  const video = getVideo(videoHash);
  if (!video) return 0;

  const userPin = getUserPins(userId).find(p => p.videoHash === videoHash);
  if (!userPin) return 0;

  const videoPins = getPinsForVideo(videoHash);
  const totalPins = videoPins.length;

  if (totalPins < 2) return 0;

  const earliestPin = Math.min(...videoPins.map(p => p.pinnedAt));
  const latestPin = Math.max(...videoPins.map(p => p.pinnedAt));
  
  const timeSinceEarliest = userPin.pinnedAt - earliestPin;
  const totalTimeSpan = latestPin - earliestPin || 1;
  const earlinessRatio = 1 - (timeSinceEarliest / totalTimeSpan);
  
  const timingMultiplier = Math.pow(earlinessRatio, 2) * 100;
  const popularityBonus = Math.log10(totalPins + 1) * 10;
  
  return Math.round(timingMultiplier + popularityBonus);
}

export function getFreshFeed(): VideoMetadata[] {
  return getAllVideos()
    .sort((a, b) => b.uploadedAt - a.uploadedAt)
    .slice(0, 50);
}

export function getPopularFeed(): VideoMetadata[] {
  const videos = getAllVideos();
  const pins = getAllPins();

  return videos
    .map(video => ({
      ...video,
      pinCount: pins.filter(p => p.videoHash === video.hash).length
    }))
    .sort((a, b) => b.pinCount - a.pinCount)
    .slice(0, 50);
}

export function getRisingFeed(): VideoMetadata[] {
  const videos = getAllVideos();
  const pins = getAllPins();
  const now = Date.now();
  const last24h = now - (24 * 60 * 60 * 1000);

  return videos
    .map(video => {
      const videoPins = pins.filter(p => p.videoHash === video.hash);
      const recentPins = videoPins.filter(p => p.pinnedAt > last24h);
      
      let tastemakerWeight = 0;
      recentPins.forEach(pin => {
        const score = calculateTastemakerScore(pin.userId);
        tastemakerWeight += Math.log10(Math.max(score, 1)) * 10;
      });
      
      return {
        ...video,
        momentum: recentPins.length,
        tastemakerMomentum: tastemakerWeight,
        pinCount: videoPins.length
      };
    })
    .filter(v => v.momentum > 0)
    .sort((a, b) => b.tastemakerMomentum - a.tastemakerMomentum)
    .slice(0, 50);
}

export function searchVideos(query: string): VideoMetadata[] {
  const videos = getAllVideos();
  const q = query.toLowerCase();

  return videos.filter(v =>
    v.title.toLowerCase().includes(q) ||
    v.description.toLowerCase().includes(q) ||
    v.tags.some(tag => tag.toLowerCase().includes(q))
  );
}

const REMOTE_PROFILES_KEY = 'authenticulture_remote_profiles';

export function saveRemoteProfile(profile: UserProfile): void {
  const stored = localStorage.getItem(REMOTE_PROFILES_KEY);
  const profiles: Record<string, UserProfile> = stored ? JSON.parse(stored) : {};
  const existing = profiles[profile.userId];
  if (!existing || existing.createdAt <= profile.createdAt) {
    profiles[profile.userId] = profile;
    localStorage.setItem(REMOTE_PROFILES_KEY, JSON.stringify(profiles));
  }
}

export function getRemoteProfile(userId: string): UserProfile | null {
  const stored = localStorage.getItem(REMOTE_PROFILES_KEY);
  if (!stored) return null;
  const profiles: Record<string, UserProfile> = JSON.parse(stored);
  return profiles[userId] || null;
}

export function getRemoteProfileByPubkey(pubkey: string): UserProfile | null {
  const stored = localStorage.getItem(REMOTE_PROFILES_KEY);
  if (!stored) return null;
  const profiles: Record<string, UserProfile> = JSON.parse(stored);
  return Object.values(profiles).find(p => p.publicKey === pubkey) || null;
}

const FOLLOWS_KEY = 'authenticulture_follows';

export interface FollowPayload {
  followerUserId: string;
  followeeUserId: string;
}

export interface FollowRecord {
  followerUserId: string;
  followeeUserId: string;
  followedAt: number;
}

export function getAllFollows(): FollowRecord[] {
  const stored = localStorage.getItem(FOLLOWS_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function saveFollow(followerUserId: string, followeeUserId: string): void {
  const follows = getAllFollows();
  if (follows.some(f => f.followerUserId === followerUserId && f.followeeUserId === followeeUserId)) return;
  follows.push({ followerUserId, followeeUserId, followedAt: Date.now() });
  localStorage.setItem(FOLLOWS_KEY, JSON.stringify(follows));
}

export function removeFollow(followerUserId: string, followeeUserId: string): void {
  const follows = getAllFollows().filter(
    f => !(f.followerUserId === followerUserId && f.followeeUserId === followeeUserId)
  );
  localStorage.setItem(FOLLOWS_KEY, JSON.stringify(follows));
}

export function isFollowing(followerUserId: string, followeeUserId: string): boolean {
  return getAllFollows().some(f => f.followerUserId === followerUserId && f.followeeUserId === followeeUserId);
}

export function getFollowing(userId: string): string[] {
  return getAllFollows().filter(f => f.followerUserId === userId).map(f => f.followeeUserId);
}

export function getFollowers(userId: string): string[] {
  return getAllFollows().filter(f => f.followeeUserId === userId).map(f => f.followerUserId);
}

export function getFollowingFeed(userId: string, limit = 50): VideoMetadata[] {
  const following = getFollowing(userId);
  if (following.length === 0) return getFreshFeed().slice(0, limit);
  return getAllVideos()
    .filter(v => following.includes(v.uploader))
    .sort((a, b) => b.uploadedAt - a.uploadedAt)
    .slice(0, limit);
}
